import { FC, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { showConfirmAlert } from '_metronic/helpers/AlertHelper'
import { useT } from '_metronic/i18n/Metronici18n'
import { FormInput, FormSelect, FormTextArea, IOption, useModal, useToast } from 'app/components'
import { getAlocacoesDispositivo } from '../../../dispositivos/core/_requests'
import { rotuloTipo } from '../../../dispositivos/core/rotulos'
import { trataErroDoModal } from '../../../dispositivos/core/errosFormulario'
import { textoOuNulo } from '../../../dispositivos/core/textoOuNulo'
import { camposDicomSchema } from '../../../dispositivos/components/modal/validation/dispositivoSchema'
import {
  buscaOpcoesDispositivosAlocaveis,
  OpcaoDispositivo,
} from '../../../dispositivos/core/opcoes'
import { TelekitView } from '../../telekits-list/core/_models'
import { alocarDispositivo } from '../../telekits-list/core/_requests'
import { ErrosGerais } from '../../../dispositivos/core/ErrosGerais'
import { RodapeModal } from '../../../dispositivos/core/RodapeModal'

type Valores = {
  dispositivo?: OpcaoDispositivo
  aeTitle: string
  modalidade: string
  descricao: string
  observacao: string
}

const CAMPOS = ['dispositivo', 'aeTitle', 'modalidade', 'descricao', 'observacao'] as const
const MAPA = { idDispositivo: 'dispositivo' }

type Preenchimento = 'preenchido' | 'falhou' | undefined

export const AlocarDispositivoModal: FC<{ telekit: TelekitView }> = ({ telekit }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const { t } = useT()
  const [erroGeral, setErroGeral] = useState<string[]>([])
  const [preenchimento, setPreenchimento] = useState<Preenchimento>()
  const buscaDispositivos = useMemo(() => buscaOpcoesDispositivosAlocaveis(t), [t])

  const formik = useFormik<Valores>({
    initialValues: {
      dispositivo: undefined,
      aeTitle: '',
      modalidade: '',
      descricao: '',
      observacao: '',
    },
    validationSchema: Yup.object().shape({
      dispositivo: Yup.object()
        .nullable()
        .required(t('pagina.telekits.dispositivos.validacao.dispositivo')),
      ...camposDicomSchema(t),
    }),
    onSubmit: async (valores, helpers) => {
      const d = valores.dispositivo?.dispositivo
      if (!d || !telekit.id) return
      setErroGeral([])
      const empresaDispositivo = d.empresa?.id
      const empresaMaleta = telekit.empresa?.id
      const outraEmpresa = empresaDispositivo !== empresaMaleta

      if (outraEmpresa && d.regime === 'VENDIDO') {
        // Mesmo bloqueio do backend, sem chamada.
        setErroGeral([t('pagina.telekits.dispositivos.vendido_outra_empresa')])
        return
      }
      if (outraEmpresa) {
        const confirmado = await showConfirmAlert({
          title: t('pagina.telekits.dispositivos.custodia_titulo'),
          text: t('pagina.telekits.dispositivos.custodia_texto', {
            tipo: rotuloTipo(t, d.tipo),
            serie: d.serie,
            origem: d.empresa?.nome ?? '—',
            destino: telekit.empresa?.nomeFantasia ?? '—',
          }),
          confirmButtonText: t('pagina.telekits.dispositivos.custodia_confirmar'),
          cancelButtonText: t('common.cancel'),
          icon: 'warning',
          danger: false,
        })
        if (!confirmado) return
      }

      try {
        await alocarDispositivo(telekit.id, {
          idDispositivo: d.id,
          aeTitle: textoOuNulo(valores.aeTitle),
          modalidade: textoOuNulo(valores.modalidade),
          descricao: textoOuNulo(valores.descricao),
          observacao: textoOuNulo(valores.observacao),
        })
        showToast({ variant: 'success', title: t('pagina.telekits.dispositivos.alocado') })
        closeModal()
      } catch (error) {
        trataErroDoModal(error, helpers, CAMPOS, setErroGeral, MAPA)
      }
    },
  })

  const { setFieldValue } = formik

  // Pré-preenche AE Title e modalidade com o 1º período do histórico que tem AE (editáveis).
  const aoEscolherDispositivo = async (valor: IOption | IOption[] | null) => {
    const opcao = valor as OpcaoDispositivo | null
    setPreenchimento(undefined)
    if (!opcao) return
    try {
      const historico = await getAlocacoesDispositivo(opcao.value)
      const anterior = historico.find((alocacao) => !!alocacao.aeTitle)
      if (anterior) {
        setFieldValue('aeTitle', anterior.aeTitle ?? '')
        setFieldValue('modalidade', anterior.modalidade ?? '')
        setPreenchimento('preenchido')
      }
    } catch {
      setPreenchimento('falhou')
    }
  }

  const ajudaDicom =
    preenchimento === 'preenchido'
      ? t('pagina.telekits.dispositivos.ajuda_preenchido')
      : preenchimento === 'falhou'
      ? t('pagina.telekits.dispositivos.ajuda_falhou')
      : undefined

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <ErrosGerais erros={erroGeral} />
      <div className='mb-5'>
        <FormSelect
          formik={formik}
          name='dispositivo'
          label={t('pagina.telekits.dispositivos.campo.dispositivo')}
          inputId='alocar-dispositivo'
          fetchOptions={buscaDispositivos}
          filtraNoServidor
          isRequired
          isClearable
          onChange={aoEscolherDispositivo}
        />
      </div>
      <div className='row g-4 mb-2'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='aeTitle'
            label={t('pagina.telekits.dispositivos.campo.ae_title')}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='modalidade'
            label={t('pagina.telekits.dispositivos.campo.modalidade')}
          />
        </div>
      </div>
      {ajudaDicom && (
        <div className='form-text text-gray-700 mb-5' role='status'>
          {ajudaDicom}
        </div>
      )}
      <div className='mb-5'>
        <FormInput
          formik={formik}
          name='descricao'
          label={t('pagina.telekits.dispositivos.campo.descricao')}
        />
      </div>
      <FormTextArea
        formik={formik}
        name='observacao'
        label={t('pagina.telekits.dispositivos.campo.observacao')}
        rows={3}
      />
      <RodapeModal
        enviando={formik.isSubmitting}
        rotuloEnviar={t('pagina.telekits.dispositivos.alocar')}
      />
    </form>
  )
}
